import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from 'zustand';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { useToast } from '@/components/ui/use-toast';
import { formatDocumentNumber } from '@/utils/formatters/format-document-number';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';
import { getAgeFromBirthday } from '@/utils/formatters/date';
import { buildOutOfAgeRangeMessage } from '@/utils/patient/build-out-of-age-range-message.util';
import { Appointment } from '@/types/appointments.types';
import { Dependent, DocumentType, Gender } from '@/types/auth-base.types';
import { authStore, resolveAuthenticatedUser } from '@/store/auth.store';
import { AppointmentOptionsModalKeys } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/variants/_components/appointment-options/appointment-options-button.component';
import { updateAppointmentPatient } from '@/services/appointment/appointment-update-patient';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { getLogger } from '@/logger/logger';

interface SelectPatientListProps {
  appointment: Appointment;
}

const formatPatientToDependent = (appointment: Appointment): Dependent => ({
  ...appointment.patient,
  contact: { phones: [appointment.customerContactPhone], email: appointment.customerContactEmail },
  documentType: appointment.patient.documentType ?? DocumentType.CPF,
  gender: appointment.patient.gender ?? Gender.NOT_INFORMED,
});

const getFirstName = (dependent: Dependent): string => {
  const display = formatToTitleCase(dependent.socialName || dependent.legalName);
  return display.split(' ')[0] ?? display;
};

export default function SelectPatientList({ appointment }: SelectPatientListProps) {
  const { handleJump, onClose } = useProgressiveModal();
  const { toast } = useToast();
  const user = useStore(authStore, (state) => state.authenticatedUser);
  const [loadingPatientId, setLoadingPatientId] = useState<string | null>(null);
  const isLoading = loadingPatientId !== null;
  const analytics = getAnalyticsPlugin();
  const logger = getLogger('appointments.select-patient-list');

  const currentPatient = useMemo(() => formatPatientToDependent(appointment), [appointment]);

  const dependents = useMemo(() => {
    if (!user || !user.dependents) {
      return [];
    }

    const userAsDependent: Dependent = {
      id: user?.id ?? '',
      legalName: user?.legalName,
      socialName: user?.socialName,
      documentNumber: user?.documentNumber,
      birthday: user.birthday,
      contact: user.contact ?? { phones: [], email: '' },
      documentType: user.documentType ?? DocumentType.CPF,
      gender: user.gender ?? Gender.NOT_INFORMED,
    };

    const allDependents = [userAsDependent, ...user.dependents];

    return allDependents?.filter(
      (dependent) =>
        formatDocumentNumber(dependent.documentNumber) !== formatDocumentNumber(currentPatient.documentNumber),
    );
  }, [user, currentPatient.documentNumber]);

  useEffect(() => {
    async function reload() {
      const authStoreState = await authStore.getState();
      if (!authStoreState.authenticatedUser) {
        await resolveAuthenticatedUser();
      }
    }

    reload();
  }, []);

  const isOutOfAgeRange = useCallback(
    (dependent: Dependent): boolean => {
      const age = getAgeFromBirthday(dependent.birthday);
      if (age > Number(appointment.maxAge)) {
        return true;
      }
      if (age < Number(appointment.minAge)) {
        return true;
      }
      return false;
    },
    [appointment.minAge, appointment.maxAge],
  );

  const outOfRangeNames = useMemo(
    () => dependents.filter(isOutOfAgeRange).map(getFirstName),
    [dependents, isOutOfAgeRange],
  );

  const warningMessage = buildOutOfAgeRangeMessage(
    outOfRangeNames,
    `${appointment.minAge} a ${appointment.maxAge} anos`,
  );

  const handlePatientSelect = async (patient: Dependent): Promise<void> => {
    setLoadingPatientId(patient.id);
    try {
      await updateAppointmentPatient({ appointmentId: appointment.id, patientId: patient.id });
      analytics.track(AnalyticsEnum.MY_APPOINTMENTS_CONFIRM_CHANGE_PATIENT);
      toast.success(`Atendimento atualizado para ${formatToTitleCase(patient.socialName || patient.legalName)}.`);
      onClose();
      location.reload();
    } catch (error: unknown) {
      logger.error('Failed to update the appointment patient', {
        appointment: { event: 'patient_change_failed', appointment_id: appointment.id, patient_id: patient.id },
        error,
      });
      toast.error('Erro ao trocar paciente. Tentar novamente.');
    } finally {
      setLoadingPatientId(null);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start justify-between gap-2">
        <Typography
          variant={TypographyVariant.MODAL_TITLE}
          data-testid={DataTestIdEnum.CHANGE_PATIENT_LIST_TITLE}
        >
          Escolha o paciente
        </Typography>
        <ModalCloseButton onClick={onClose} disabled={isLoading} className="shrink-0" label="Fechar modal" />
      </div>

      <Typography
        variant={TypographyVariant.BODY_TEXT}
        className="text-gray-700"
        data-testid={DataTestIdEnum.CHANGE_PATIENT_LIST_SUBTITLE}
      >
        O agendamento deve ser feito em nome de quem será atendido.
      </Typography>

      {warningMessage && (
        <div className="flex w-full items-center gap-3 rounded-2xl border border-warning-300 bg-warning-100 py-3 pl-3 pr-4 shadow-floating">
          <Icon icon={faCircleExclamation} className="flex-shrink-0 text-xl text-warning-600" />
          <p className="text-base font-semibold text-warning-700">{warningMessage}</p>
        </div>
      )}

      {dependents.length > 0
        ? (
            <div className="flex flex-col gap-4">
              {dependents.map((dependent) => {
                const outOfRange = isOutOfAgeRange(dependent);
                return (
                  <Button
                    key={`key-option-patient-${dependent.legalName}-${dependent.documentNumber}`}
                    disabled={outOfRange || isLoading}
                    isLoading={loadingPatientId === dependent.id}
                    variant="secondary"
                    color="primary"
                    className="flex flex-col gap-1 py-3 whitespace-normal break-words text-center"
                    onClick={() => handlePatientSelect(dependent)}
                  >
                    <Typography
                      variant={TypographyVariant.BODY_TEXT}
                      className="font-semibold text-inherit text-lg"
                    >
                      {formatToTitleCase(dependent?.socialName || dependent.legalName)}
                    </Typography>
                    <Typography variant={TypographyVariant.BODY_TEXT} className="text-inherit text-lg">
                      {formatDocumentNumber(dependent.documentNumber)}
                    </Typography>
                  </Button>
                );
              })}
            </div>
          )
        : (
            <Typography
              variant={TypographyVariant.BODY_TEXT}
              className="text-gray-700"
              data-testid={DataTestIdEnum.CHANGE_PATIENT_LIST_EMPTY_STATE}
            >
              Você não tem dependentes cadastrados.
            </Typography>
          )}

      <Button
        variant="outline"
        color="primary"
        data-testid={DataTestIdEnum.CHANGE_PATIENT_CREATE_ANOTHER_BUTTON}
        onClick={() => handleJump({ key: AppointmentOptionsModalKeys.CHANGE_PATIENT_CREATE })}
      >
        Agendar para outra pessoa
      </Button>
    </div>
  );
}
